import { useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { directorySearchStore, useDirectorySearchState } from '../state';

export const DIRECTORY_SEARCH_DEBOUNCE_MS = 750;

/**
 * Drives the directory's search box: the text on screen and when it becomes a query.
 *
 * Typing updates the box at once and the query once the typing pauses. The pause is long enough
 * that a name is usually finished before a request goes out, so a burst of typing costs one
 * search instead of several. Pressing the search key or clearing the box applies the query
 * immediately, since those are deliberate acts rather than keystrokes.
 *
 * A search still waiting out its pause when the box unmounts is applied, so the text the store
 * keeps is never left unsearched.
 *
 * A pause that ends after the text was cleared or reset from elsewhere is dropped: it carries
 * text the box no longer holds and would put the old search back.
 */
export function useDirectorySearchBox() {
  const value = useDirectorySearchState((state) => state.searchInput);

  const publish = useDebouncedCallback((typed: string) => {
    const { searchInput, applySearch } = directorySearchStore.getState();

    if (searchInput === typed) {
      applySearch(typed);
    }
  }, DIRECTORY_SEARCH_DEBOUNCE_MS);

  useEffect(
    () => () => {
      publish.flush();
    },
    [publish]
  );

  const onChangeText = useCallback(
    (typed: string) => {
      directorySearchStore.getState().setSearchInput(typed);

      publish(typed);
    },
    [publish]
  );

  const onSubmit = useCallback(() => {
    publish.cancel();

    const { searchInput, applySearch } = directorySearchStore.getState();

    applySearch(searchInput);
  }, [publish]);

  const onClear = useCallback(() => {
    publish.cancel();

    const { setSearchInput, applySearch } = directorySearchStore.getState();

    setSearchInput('');

    applySearch('');
  }, [publish]);

  return { value, onChangeText, onSubmit, onClear };
}
